import { AfterViewInit, Component, inject, OnInit, ViewChild } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { DsdForm } from '@dsd/library-angular';
import { AccessibiliteService } from '@services/accessibilite.service';
import { FormulaireMouvementService } from '@services/formulaire-mouvement/formulaire-mouvement.service';
import { GabaritService } from '@services/gabarit/gabarit.service';
import { GlobalStoreService } from '@services/global-store/global-store.service';
import { NavigationService } from '@services/navigation/navigation.service';
import { FormulaireCoordonneesMouvementAdresseBuilder } from 'src/app/models/formulaire/formulaire-coordonnees-mouvement-adresse-builder.model';
import { FormulaireCoordonneesMouvementAdresseModel } from 'src/app/models/formulaire/formulaire-coordonnees-mouvement-adresse.model';
import { Anys } from '../../util/anys';
import { GestionTitreService } from '@services/navigation/gestion-titre.service';
import { NavigationEnd, Router } from '@angular/router';
import { DsdFormSubmitEvent } from '@dsd/library/dist/types/components/form';
import { TranslateService } from '@ngx-translate/core';
import { DsdFormSummaryError } from '@dsd/library/dist/types/components/form-error-summary/form-error-summary.model';
import { CybermetrieCoordonneesService } from '@services/cybermetrie/cybermetrie.service';
import { CybermetrieEnum } from '../../constantes/cybermetrie.enum';
import { ContexteModificationEnum } from '../../constantes/contexte-modification.enum';
@Component({
  selector: 'app-modifier-adresse',
  templateUrl: './modifier-adresse.component.html',
  standalone: false,
})
export class ModifierAdresseComponent implements OnInit, AfterViewInit {
  private readonly globalStoreService = inject(GlobalStoreService);
  private readonly formulaireMouvementService = inject(FormulaireMouvementService);
  private readonly navigationService = inject(NavigationService);
  private readonly gabaritService = inject(GabaritService);
  private readonly accessibiliteService = inject(AccessibiliteService);
  private readonly gestionTitreService = inject(GestionTitreService);
  private readonly router = inject(Router);
  private readonly translateService = inject(TranslateService);
  private readonly cybermetrieCoordonneesService = inject(CybermetrieCoordonneesService);

  @ViewChild(DsdForm) dsdForm!: DsdForm;

  isAppMobile!: boolean;
  formGroup: FormGroup<FormulaireCoordonneesMouvementAdresseModel> | null = null;

  ngOnInit(): void {
    this.gestionTitreService.addTitreOnglet('titreOnglet.modifierAdresse');
    this.isAppMobile = this.gabaritService.isAppMobile();
    this.globalStoreService.saveContexteModification(ContexteModificationEnum.ADRESSE);
    if (Anys.notNullOrUndef(this.formulaireMouvementService.getFormAdresse())) {
      this.formGroup =
        this.formulaireMouvementService.getFormAdresse() as FormGroup<FormulaireCoordonneesMouvementAdresseModel>;
    } else {
      this.globalStoreService.getCoordonneesMouvement$().subscribe((membre) => {
        const detentionDGAGApparie = membre?.indicateurs?.indicateurDetentionDGAG ?? false;
        this.formGroup = FormulaireCoordonneesMouvementAdresseBuilder.buildAdresse(
          membre?.adresseCourante,
          detentionDGAGApparie,
        );
        this.cybermetrieCoordonneesService.transactionDebuter(
          CybermetrieEnum.LABEL_MODIFIER_ADRESSE,
          detentionDGAGApparie
            ? CybermetrieEnum.EXTRA_ATTRIBUT_DGAG_APPARIE
            : CybermetrieEnum.EXTRA_ATTRIBUT_DGAG_NON_APPARIE,
        );
      });
    }
  }

  ngAfterViewInit(): void {
    this.router.events.subscribe((val) => {
      if (val instanceof NavigationEnd) {
        let anchor = val.urlAfterRedirects.lastIndexOf('#');
        if (anchor >= 0 && anchor < val.urlAfterRedirects.length - 1) {
          const elementId = val.urlAfterRedirects.substring(anchor + 1);
          this.accessibiliteService.setFocus(`#${elementId}`);
        } else {
          this.accessibiliteService.setFocus('#titreModification');
        }
      }
    });
    if (globalThis.location.hash) {
      const elementId = globalThis.location.hash.substring(1);
      this.accessibiliteService.setFocus(`#${elementId}`);
    } else {
      this.accessibiliteService.setFocus('#titreModification');
    }
  }

  annulerModification(): void {
    this.globalStoreService.saveEtatAfficherModaleAnnuler(true);
  }

  async naviguerVersPageValidation(event: CustomEvent<DsdFormSubmitEvent>): Promise<void> {
    if (this.formGroup) {
      const errors = await this.dsdForm.validate();
      if (errors.length === 0 && this.formGroup.valid) {
        this.formulaireMouvementService.setFormAdresse(this.formGroup);
        this.navigationService.naviguerVersValidationAdresse();
      } else {
        this.personaliserMessageErreur(event);
      }
    }
  }

  private personaliserMessageErreur(event: CustomEvent<DsdFormSubmitEvent>): void {
    const listIdentifiers = new Set([
      'changementFait-fieldset',
      'detention-fieldset',
      'preferenceAppel-fieldset',
      'preferenceMoment-fieldset',
    ]);
    const errors = event.detail.errors;
    if (errors) {
      const labelToModify = (errors as DsdFormSummaryError[]).find((el: DsdFormSummaryError) =>
        listIdentifiers.has(el['id']),
      );
      if (labelToModify) {
        labelToModify['text'] = this.getTraduction(labelToModify.id);
      }
      this.cybermetrieErreurs(errors as DsdFormSummaryError[]);
    }
  }

  private cybermetrieErreurs(errors: DsdFormSummaryError[]): void {
    let texteErreurs: string[] = [];
    errors.forEach((erreurForm) => {
      if (erreurForm.text) {
        texteErreurs.push(erreurForm.text);
      }
    });
    this.cybermetrieCoordonneesService.transactionErreurUtilisateur(
      CybermetrieEnum.LABEL_MODIFIER_ADRESSE,
      texteErreurs,
    );
  }

  private getTraduction(cleTraduction: string): string {
    let texteTraduction = '';
    this.translateService
      .get('modifierAdresse.questionnaireDGAG.sommaireErreur.' + cleTraduction)
      .subscribe((texte: string) => (texteTraduction = texte));
    return texteTraduction;
  }
}
